/**
 * @fileoverview System sync collection configuration — the authoritative
 *               mapping between system-web Dexie table names and system
 *               MongoDB collection names, plus the system-web push allowlist.
 * @module sync-config/system
 *
 * Responsibilities:
 * - Define `SYSTEM_COLLECTION_MAP` (Dexie key → system MongoDB collection).
 * - Define `SYSTEM_PUSH_ALLOWED_COLLECTIONS` (which Dexie keys may push).
 * - Define `SYSTEM_COLLECTION_DISPLAY_NAMES` for sync UI surfaces.
 *
 * Dependencies:
 * - None — pure configuration.
 *
 * Architecture:
 * - Used by `system-web` (`SystemDBProvider`, WebSocket subscription) and by
 *   `system-api` (`/sync/*` routes, WebSocket route map, change watcher).
 * - System-web prefixes user/lookup/group keys with `system_` to disambiguate
 *   them from tenant-side equivalents that share the same MongoDB collection
 *   name. These prefixes match the production system-web Dexie schema.
 *
 * Notes:
 * - When adding a collection: update the map, optionally add to the push
 *   allowlist, add a display name, and update the Dexie schema in
 *   `web/system-web/src/db/systemDb.ts`. Drift is asserted in tests.
 */

/**
 * System collection map — Dexie table name → server collection name.
 *
 * NOTE on key naming convention:
 *   System-web prefixes user/lookup/group keys with `system_` (e.g.
 *   `system_users`, `system_lookups`) to disambiguate them from the
 *   tenant-side equivalents that share the same MongoDB collection name.
 *   These prefixes match the production system-web Dexie schema in
 *   `web/system-web/src/db/systemDb.ts`. Do NOT rename without a Dexie
 *   migration — the keys are how UI services find their tables.
 *
 * NOTE on `import_runs`:
 *   Import runs are system-authoritative lifecycle documents. system-web may
 *   seed local draft/progress rows in Dexie for UX continuity, but upload,
 *   preview, dry-run, commit, cancel, workers, and tenant-api own authoritative
 *   writes through dedicated online endpoints. Excluding `importRuns` from
 *   push prevents client/server progress echo loops.
 *
 * NOTE on `code_set_raw_artifacts`:
 *   Resolves drift D-4 (Phase 0 inventory). This is a system-only
 *   collection — sync-pullable for read access but EXCLUDED from
 *   `SYSTEM_PUSH_ALLOWED_COLLECTIONS` (system-api enforces the same
 *   exclusion server-side at `routes/syncRoutes.ts`).
 *
 * NOTE on `audit_logs`:
 *   System-web does NOT currently sync audit logs through this map. If
 *   a future system-admin audit viewer requires Dexie-backed reads, add
 *   `audit_logs: 'auditLogs'` here AND a Dexie table in systemDb.ts AND
 *   add `'auditLogs'` to SYSTEM_PUSH_ALLOWED_COLLECTIONS (or omit if
 *   read-only).
 */
export const SYSTEM_COLLECTION_MAP = {
  tenants:                  'tenants',
  organizations:            'organizations',
  system_users:             'users',
  system_user_groups:       'userGroups',
  system_roles:             'systemRoles',
  organization_roles:       'organizationRoles',
  tiers:                    'tiers',
  feature_flags:            'featureFlags',
  modules:                  'modules',
  system_lookups:           'lookups',
  system_lookup_groups:     'lookupGroups',
  system_lookup_categories: 'lookupCategories',
  onboarding_drafts:        'onboardingDrafts',
  import_templates:         'importTemplates',
  import_runs:              'importRuns',
  report_definitions:       'reportDefinitions',
  report_templates:         'reportTemplates',
  report_schedules:         'reportSchedules',
  email_templates:          'emailTemplates',
  uploaded_files:           'uploadedFiles',
  generated_documents:      'generatedReports',
  code_set_raw_artifacts:   'codeSetRawArtifacts',
  system_inspection_templates: 'systemInspectionTemplates',
  system_forms:             'systemForms',
  // Background jobs are system-authoritative: workers write status/progress
  // and clients read via live sync. NOT in SYSTEM_PUSH_ALLOWED — client UIs
  // must never push job state back. Mongo change stream on `backgroundJobs`
  // becomes the real-time progress channel for the tether indicator.
  background_jobs:          'backgroundJobs',
  // LM-012: Legacy migration tables (read-only client sync — §7.2 of implementation plan).
  // All three are server-authoritative. The client triggers actions via HTTP endpoints;
  // system-api is the only writer. Push is blocked at the server allowlist layer.
  legacy_migration_receipts:           'legacyMigrationReceipts',
  legacy_migration_runs:               'legacyMigrationRuns',
  legacy_migration_manifest_overrides: 'legacyMigrationManifestOverrides',
  // LM-020: Schema manifest (read-only client sync). One doc per legacy database.
  // Admin edits via POST /api/system/legacy-migration/schema-manifest/:legacyDb.
  // Push blocked — server is the only writer.
  legacy_schema_manifest_tables:       'legacySchemaManifestTables',
  // G-087: Dataset Template System (§3.11.6 — read-only client sync).
  // All writes go through /api/system/legacy-migration/dataset-templates/* routes.
  // Push blocked — server is the only writer.
  legacy_migration_dataset_templates:  'legacyMigrationDatasetTemplates',
  // Clusters registry — server-authoritative, read-only client sync.
  // system-web reads cluster metadata (name, region, status) to display the
  // real cluster name on the department configuration tab instead of the
  // denormalized (and historically wrong) tenant.dataLocation.clusterName.
  // Push blocked — server is the only writer. The pull projection in
  // system-api syncRoutes strips `connectionString` so credentials never
  // reach the client.
  clusters:                            'clusters',
  // AI model registry — server-authoritative, read-only client sync.
  // system-web reads model catalog and task assignments for the Settings →
  // AI Models admin UI. Push blocked — all writes go through system-api
  // /api/system/ai-models and /api/system/ai-task-assignments routes.
  ai_models:                           'aiModels',
  ai_task_assignments:                 'aiTaskAssignments',
  // Regrid master-cache GeoParquet file registry — server-authoritative.
  // The upload route creates the record and the cache upload worker owns all
  // progress and terminal writes. system-web may render a local placeholder,
  // but only sync pull can reconcile it with MongoDB. This prevents a stale
  // placeholder from overwriting worker results after checkpoint recovery.
  geo_cache_files:                     'geoCacheFiles',
  // Inspection Programs & Entity Scheduling (#737 Phase 6, plan §11.5).
  // Both are server-authoritative system catalogs — system-web manages them
  // through /api/system-inspection-programs and /api/schedule-rule-catalog
  // routes, which then propagate to tenant DBs via the canonical replay.
  // Dexie mirrors exist so system-web pages read local-first; push is blocked.
  system_inspection_programs:          'systemInspectionPrograms',
  system_schedule_rule_catalog:        'systemScheduleRuleCatalog',
} as const;

/**
 * System collections allowed to be pushed from client to server.
 *
 * Derived from SYSTEM_COLLECTION_MAP minus server-authoritative collections
 * (`importRuns`, `codeSetRawArtifacts`, `backgroundJobs`). system-api applies
 * the same exclusion server-side at `routes/syncRoutes.ts` SYSTEM_PUSH_ALLOWED.
 */
const SYSTEM_SERVER_AUTHORITATIVE: ReadonlySet<string> = new Set([
  'importRuns',
  'codeSetRawArtifacts',
  'backgroundJobs',
  // LM-012: Legacy migration collections — server-authoritative; push is blocked.
  'legacyMigrationReceipts',
  'legacyMigrationRuns',
  'legacyMigrationManifestOverrides',
  // LM-020: Schema manifest — server-authoritative; push is blocked.
  'legacySchemaManifestTables',
  // G-087: Dataset Template System — server-authoritative; push is blocked.
  'legacyMigrationDatasetTemplates',
  // Clusters registry — server-authoritative; push is blocked.
  // Clients read cluster metadata for display only; cluster registration
  // and connection strings are managed exclusively via system-api routes.
  'clusters',
  // AI model registry — server-authoritative; push is blocked.
  // Clients read model catalog and task assignments for display only;
  // all writes go through system-api /api/system/ai-models routes.
  'aiModels',
  'aiTaskAssignments',
  // Regrid master-cache file registry — server-authoritative. The upload route
  // creates it and the worker owns progress/completion; client placeholders
  // are local-only and must never overwrite terminal worker state.
  'geoCacheFiles',
  // #737 Phase 6 — inspection-program + rule-catalog are system catalogs
  // owned by system-api routes; edits propagate to tenants via the canonical
  // replay. Client push is blocked — Dexie copies are pull-only read mirrors.
  'systemInspectionPrograms',
  'systemScheduleRuleCatalog',
]);
export const SYSTEM_PUSH_ALLOWED_COLLECTIONS: ReadonlySet<string> = new Set(
  Object.values(SYSTEM_COLLECTION_MAP).filter((mongoName) => !SYSTEM_SERVER_AUTHORITATIVE.has(mongoName)),
);

/**
 * Human-readable display names for sync UI.
 * Keys match SYSTEM_COLLECTION_MAP (Dexie table names).
 */
export const SYSTEM_COLLECTION_DISPLAY_NAMES: Record<string, string> = {
  tenants:                  'Tenants',
  organizations:            'Organizations',
  system_users:             'System Users',
  system_user_groups:       'System User Groups',
  system_roles:             'System Roles',
  organization_roles:       'Organization Roles',
  tiers:                    'Tiers',
  feature_flags:            'Feature Flags',
  modules:                  'Modules',
  system_lookups:           'System Lookups',
  system_lookup_groups:     'System Lookup Groups',
  system_lookup_categories: 'System Lookup Categories',
  onboarding_drafts:        'Onboarding Drafts',
  import_templates:         'Import Templates',
  import_runs:              'Import Runs',
  report_definitions:       'Report Definitions',
  report_templates:         'Report Templates',
  report_schedules:         'Report Schedules',
  email_templates:          'Email Templates',
  uploaded_files:           'Uploaded Files',
  generated_documents:      'Generated Documents',
  code_set_raw_artifacts:   'Code Set Raw Artifacts',
  system_inspection_templates: 'System Inspection Templates',
  system_forms:             'System Forms',
  background_jobs:          'Background Jobs',
  // LM-012: Legacy migration display names
  legacy_migration_receipts:           'Legacy Migration Receipts',
  legacy_migration_runs:               'Legacy Migration Runs',
  legacy_migration_manifest_overrides: 'Legacy Migration Manifest Overrides',
  // LM-020: Schema manifest display name
  legacy_schema_manifest_tables:       'Legacy Schema Manifest Tables',
  // G-087: Dataset Template System display name
  legacy_migration_dataset_templates:  'Legacy Migration Dataset Templates',
  // Clusters registry display name
  clusters:                            'Clusters',
  // AI model registry display names
  ai_models:                           'AI Models',
  ai_task_assignments:                 'AI Task Assignments',
  // Regrid master-cache file registry display name
  geo_cache_files:                     'Geo Cache Files',
  // #737 Phase 6 display names
  system_inspection_programs:          'System Inspection Programs',
  system_schedule_rule_catalog:        'Schedule Rule Catalog',
};
